import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Slider from "react-slick";
import useIsDesktop from "@/hooks/useIsDesktop";
import useSlickArrows from "@/hooks/useSlickArrows";
import styles from "./ExclusiveOffers.module.css";
import { fetchCouponList } from "@/services/api/common";
import { isIndianUser } from "@/utils/getCountryCode";
import { withRegionPath } from "@/utils/session";
import { getCouponDetailPath } from "@/utils/couponHelpers";
import { MdChevronLeft, MdChevronRight, MdKeyboardArrowRight } from "react-icons/md";
import { BsChevronRight } from "react-icons/bs";
import appendLoopingSlickDots from "@/Common/LoopingSlickDots";

const BTNdata = [
  { id: 1, btn: "All Offers", mobileBtn: "All", moduleId: "ALL" },
  { id: 2, btn: "Holidays", moduleId: "1" },
  { id: 4, btn: "Stays", moduleId: "3" },
  { id: 3, btn: "Flights", moduleId: "4" },
  { id: 5, btn: "Activities", moduleId: "2" },
];

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OfferCard({ offer }) {
  const badgeText = offer.couponTag;
  const detailHref = offer.couponSlug
    ? withRegionPath(getCouponDetailPath(offer.couponSlug))
    : "";

  const cardContent = (
    <article className={styles.card}>
      <div className={styles.cardBody}>
        <div className={styles.badgeSlot}>
          {badgeText ? <span className={styles.badge}>{badgeText}</span> : null}
        </div>
        <h3 className={styles.cardTitle}>{offer.name}</h3>
        <p className={styles.cardDesc}>{offer.description}</p>
      </div>
      <div className={styles.codeBox}>
        <img src={"/assets/icons/TicketIcon.svg"} alt="" />
        <span className={styles.codeLabel}>
          <span className={`${styles.codePrefix} ${styles.codePrefixMob}`}>CODE: </span>
          <span className={styles.codePrefix}> {offer.code}</span>
        </span>
      </div>
    </article>
  );

  if (!detailHref) return cardContent;

  return (
    <Link href={detailHref} className={styles.cardLink}>
      {cardContent}
    </Link>
  );
}

const MOBILE_SLIDES_BREAKPOINT = 640;

function getTabsForRegion(isIndia) {
  return isIndia
    ? BTNdata.filter((tab) => tab.moduleId === "ALL" || tab.moduleId === "1")
    : BTNdata;
}

function getTabIndexForModule(tabs, moduleId) {
  const index = tabs.findIndex((tab) => tab.moduleId === moduleId);
  return index >= 0 ? index : 0;
}

function scrollActiveTabHorizontally(tabsContainer, activeTabEl, selectedIndex, totalTabs) {
  if (!tabsContainer || !activeTabEl) return;

  const isFirstTab = selectedIndex === 0;
  const isLastTab = selectedIndex === totalTabs - 1;

  if (isFirstTab) {
    tabsContainer.scrollTo({ left: 0, behavior: "smooth" });
    return;
  }

  if (isLastTab) {
    tabsContainer.scrollTo({
      left: tabsContainer.scrollWidth - tabsContainer.clientWidth,
      behavior: "smooth",
    });
    return;
  }

  const tabLeft = activeTabEl.offsetLeft;
  const tabWidth = activeTabEl.offsetWidth;
  const containerWidth = tabsContainer.clientWidth;
  const targetLeft = tabLeft - (containerWidth - tabWidth) / 2;

  tabsContainer.scrollTo({
    left: Math.max(0, targetLeft),
    behavior: "smooth",
  });
}

export default function ExclusiveOffers({
  initialCouponList = null,
  defaultModuleId = "ALL",
}) {
  const [couponList, setCouponList] = useState(initialCouponList);
  const [isLoaded, setIsLoaded] = useState(initialCouponList != null);
  const [isIndia, setIsIndia] = useState(() =>
    typeof window !== "undefined" ? isIndianUser() : true
  );
  const filteredBTNdata = useMemo(
    () => getTabsForRegion(isIndia),
    [isIndia]
  );
  const [selectedBtn, setSelectedBtn] = useState(() =>
    getTabIndexForModule(
      getTabsForRegion(typeof window !== "undefined" ? isIndianUser() : true),
      defaultModuleId
    )
  );
  const { isDesktop, mounted } = useIsDesktop(900);
  const [mobileViewportTick, setMobileViewportTick] = useState(0);
  const sliderRef = useRef(null);
  const tabsRef = useRef(null);
  const tabRefs = useRef([]);
  useEffect(() => {
    setIsIndia(isIndianUser());
  }, []);

  useEffect(() => {
    if (!mounted || isDesktop) return;

    const media = window.matchMedia(`(max-width: ${MOBILE_SLIDES_BREAKPOINT}px)`);
    const syncSlides = () => setMobileViewportTick((tick) => tick + 1);

    syncSlides();
    media.addEventListener("change", syncSlides);
    return () => media.removeEventListener("change", syncSlides);
  }, [mounted, isDesktop]);

  const mobileSlidesToShow = useMemo(() => {
    if (!mounted || isDesktop || typeof window === "undefined") return 3;
    return window.innerWidth <= MOBILE_SLIDES_BREAKPOINT ? 2 : 3;
  }, [mounted, isDesktop, mobileViewportTick]);

  useEffect(() => {
    if (initialCouponList != null) return;

    fetchCouponList()
      .then((response) => {
        setCouponList(response?.data || null);
      })
      .catch((error) => {
        console.error("Failed to load coupon list:", error);
        setCouponList(null);
      })
      .finally(() => {
        setIsLoaded(true);
      });
  }, [initialCouponList]);

  const activeCoupons = couponList?.activeCoupons || [];

  useEffect(() => {
    if (selectedBtn >= filteredBTNdata.length) {
      setSelectedBtn(getTabIndexForModule(filteredBTNdata, defaultModuleId));
    }
  }, [filteredBTNdata, defaultModuleId, selectedBtn]);

  useEffect(() => {
    if (!mounted || !isLoaded) return undefined;

    const tabsContainer = tabsRef.current;
    const activeTabEl = tabRefs.current[selectedBtn];
    if (!tabsContainer || !activeTabEl) return undefined;

    const frame = window.requestAnimationFrame(() => {
      scrollActiveTabHorizontally(
        tabsContainer,
        activeTabEl,
        selectedBtn,
        filteredBTNdata.length
      );
    });

    return () => window.cancelAnimationFrame(frame);
  }, [selectedBtn, mounted, isLoaded, filteredBTNdata.length]);

  const filteredOffers = useMemo(() => {
    const selectedModule = filteredBTNdata[selectedBtn]?.moduleId;
    if (!selectedModule || selectedModule === "ALL") return activeCoupons;

    return activeCoupons.filter((item) => {
      const modules = item.moduleId?.split(",") || [];
      return modules.includes(selectedModule);
    });
  }, [activeCoupons, filteredBTNdata, selectedBtn]);

  const desktopSlidesToShow = 3;
  const activeTab = filteredBTNdata[selectedBtn]?.btn || "All Offers";
  const { canPrev, canNext, slickArrowEvents } = useSlickArrows(
    filteredOffers.length,
    desktopSlidesToShow,
    activeTab
  );

  const desktopSlickSettings = {
    dots: false,
    infinite: false,
    centerMode: false,
    speed: 400,
    slidesToShow: desktopSlidesToShow,
    slidesToScroll: 1,
    arrows: false,
    swipe: false,
    draggable: false,
    touchMove: false,
    ...slickArrowEvents,
    responsive: [
      {
        breakpoint: 1100,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      }
    ],
  };

  const mobileSlickSettings = {
    dots: filteredOffers.length > mobileSlidesToShow,
    infinite: false,
    centerMode: false,
    speed: 400,
    slidesToShow: mobileSlidesToShow,
    slidesToScroll: 1,
    arrows: false,
    swipe: true,
    draggable: true,
    touchMove: true,
    dotsClass: `slick-dots ${styles.carouselDots}`,
    appendDots: appendLoopingSlickDots(`slick-dots ${styles.carouselDots}`),
  };

  const fewDesktopSlides = filteredOffers.length <= desktopSlidesToShow;
  const hasItems = filteredOffers.length > 0;
  const showEmpty = isLoaded && !hasItems;

  const offerSlides = filteredOffers.map((offer) => (
    <div key={offer.id} className={styles.slickSlide}>
      <OfferCard offer={offer} />
    </div>
  ));

  return (
    <section className={`${styles.section} standard-padding`}>
      <div className={styles.header}>
        <h2 className={"standard-heading"}>Travel Offers</h2>

        {hasItems ? (
          <div className="headerArrows">
            <button
              type="button"
              className="headerArrowBtn"
              aria-label="Previous"
              disabled={!canPrev}
              onClick={() => {
                if (typeof sliderRef.current?.slickPrev === "function") {
                  sliderRef.current.slickPrev();
                }
              }}
            >
              <MdChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="headerArrowBtn"
              aria-label="Next"
              disabled={!canNext}
              onClick={() => {
                if (typeof sliderRef.current?.slickNext === "function") {
                  sliderRef.current.slickNext();
                }
              }}
            >
              <MdChevronRight size={22} />
            </button>
          </div>
        ) : null}

        {hasItems ? (
          <a href={withRegionPath("/coupons")} className={styles.viewAll}>
            View All
            <ArrowIcon />
          </a>
        ) : null}
      </div>

      {isLoaded ? (
        <>
          <div className={styles.tabsShell}>
            <div
              ref={tabsRef}
              className={styles.tabs}
              role="tablist"
              aria-label="Offer categories"
            >
              {filteredBTNdata.map((tab, index) => (
                <button
                  key={tab.id}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={selectedBtn === index}
                  className={`${styles.tab} ${selectedBtn === index ? styles.tabActive : ""}`}
                  onClick={() => {
                    setSelectedBtn(index);
                    if (typeof sliderRef.current?.slickGoTo === "function") {
                      sliderRef.current.slickGoTo(0);
                    }
                  }}
                >
                  <span className={styles.tabLabelDesktop}>{tab.btn}</span>
                  <span className={styles.tabLabelMobile}>
                    {tab.mobileBtn || tab.btn}
                  </span>
                </button>
              ))}
              <a href={withRegionPath("/coupons")} className={styles.viewAllDesktop}>
                View All
                <BsChevronRight />
              </a>
              {/* <button 
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selectedBtn === index}
                className={`${styles.tab} ${selectedBtn === index ? styles.tabActive : ""}`}
                onClick={() => {
                  router
                }}
              >
                <span className={styles.tabLabelDesktop}>View All <MdKeyboardArrowRight /></span>
              </button> */}
            </div>
          </div>

          {showEmpty ? (
            <p className="nodata">No data found</p>
          ) : mounted ? (
            isDesktop ? (
              <div
                className={`${styles.slickWrap} ${fewDesktopSlides ? styles.alignLeft : ""}`}
                key={activeTab}
              >
                <Slider ref={sliderRef} {...desktopSlickSettings}>
                  {offerSlides}
                </Slider>
              </div>
            ) : (
              <div className={`${styles.slickWrap} ${styles.slickWrapMobile}`} key={`${activeTab}-mobile-${mobileSlidesToShow}`}>
                <Slider {...mobileSlickSettings}>{offerSlides}</Slider>
              </div>
            )
          ) : (
            <div className={styles.sliderFallback}>
              {filteredOffers.slice(0, 3).map((offer) => (
                <OfferCard key={offer.id} offer={offer} />
              ))}
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
